import React from 'react'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'

interface AnimatedSectionProps {
  children: React.ReactNode
  /** Тип анимации */
  animation?: 'fadeIn' | 'slideUp' | 'slideLeft' | 'slideRight' | 'growUp'
  /** Задержка перед анимацией в миллисекундах */
  delay?: number
  /** Длительность анимации в миллисекундах */
  duration?: number
  /** Дополнительные CSS классы */
  className?: string
  /** Дополнительные CSS стили */
  style?: React.CSSProperties
  /** Процент видимости для срабатывания (0-1) */
  threshold?: number
  /** Отступы от viewport для срабатывания */
  rootMargin?: string
  /** Расстояние движения для slide анимаций в пикселях */
  distance?: number
}

/**
 * Компонент для анимированного появления блоков при скроллинге
 * Поддерживает различные типы анимаций с настраиваемыми параметрами
 */
const AnimatedSection: React.FC<AnimatedSectionProps> = ({
  children,
  animation = 'fadeIn',
  delay = 100,
  duration = 1000,
  className = '',
  style = {},
  threshold = 0.1,
  rootMargin = '0px 0px -100px 0px',
  distance = 30
}) => {
  const { ref, isIntersecting } = useIntersectionObserver({
    threshold,
    rootMargin
  })

  const getAnimationStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      transition: `all ${duration}ms ease-out`,
      transitionDelay: `${delay}ms`,
      ...(animation === 'growUp' ? { transformOrigin: 'bottom' } : null),
      ...style
    }

    if (!isIntersecting) {
      switch (animation) {
        case 'fadeIn':
          return { ...baseStyles, opacity: 0 }
        case 'slideUp':
          return { ...baseStyles, opacity: 0, transform: `translateY(${distance}px)` }
        case 'slideLeft':
          return { ...baseStyles, opacity: 0, transform: `translateX(-${distance}px)` }
        case 'slideRight':
          return { ...baseStyles, opacity: 0, transform: `translateX(${distance}px)` }
        case 'growUp':
          return { ...baseStyles, opacity: 0, transform: 'scaleY(0)' }
        default:
          return { ...baseStyles, opacity: 0 }
      }
    }

    if (animation === 'growUp') {
      return { ...baseStyles, opacity: 1, transform: 'scaleY(1)' }
    }

    return { ...baseStyles, opacity: 1, transform: 'translate(0, 0)' }
  }

  return (
    <div ref={ref} style={getAnimationStyles()} className={className}>
      {children}
    </div>
  )
}

export default AnimatedSection
